// MiniDrawer.jsx
import * as React from "react";
import { alpha, styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AppsIcon from "@mui/icons-material/Apps";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import IconMask from "../IconMask";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { app } from "../../services";
import {
  getPayloadLink,
  getPayloadBody,
  getPayloadTitle,
  registerForegroundPushHandler,
  requestAndSyncPushToken,
  resolveNotificationLink,
} from "../../services/pushNotifications";

import {
  Alert,
  Button,
  ButtonGroup,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  ListItemSecondaryAction,
  Paper,
  Popover,
  Snackbar,
  useMediaQuery,
} from "@mui/material";
import useMeasure from "react-use-measure";
import Login from "../Login";
import {
  removeGlobalUser,
  removeInstitution,
  removeToken,
  setActiveApplication,
  setInstitution,
  setUserTools,
} from "../../redux/session";
import CatalogsIconButton from "../customForms/CatalogsIconButton";
import { instituciones, usuarios } from "../../constants/models";
import { Helmet } from "react-helmet";
import "./MiniDrawer.css";
import { DeleteForeverOutlined } from "@mui/icons-material";
import NotificationComponent from "../../common/NotificationComponent";
import { Zoom } from "@material-ui/core";
import { TransitionUp } from "../../common/TransitionComponents";
import { socket, socketApp } from "../../services";
import { reconnectAndAuthenticate } from "../../services/socketReconnect";
import {
  applicationOptions,
  defaultToolIds,
  getInitialDashboardPath,
  getUserPreferenceId,
  routes,
  selectableToolRoutes,
} from "../../constants/navigation";
import AppBarActionsContext from "./AppBarActionsContext";

const drawerWidth = 240;

/** =========================
 *  Mixins
 *  ========================= */
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: 0,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

/** =========================
 *  Styled
 *  ========================= */
const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

// AppBar transparente (si quieres glass, lo cambio)
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "mobile",
})(({ theme, open, mobile }) => ({
  zIndex: mobile ? theme.zIndex.appBar : theme.zIndex.drawer + 1,
  background: "transparent",
  boxShadow: "none",
  border: "none",
  backdropFilter: "none",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && !mobile && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

// Desktop stays compact/permanent; mobile becomes an overlay surface.
const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "mobile",
})(({ theme, open, mobile }) => ({
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",

  ...(mobile
    ? {
        "& .MuiDrawer-paper": {
          width: `min(${drawerWidth}px, 86vw)`,
          background: `${
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.default, 0.72)
              : alpha(theme.palette.background.paper, 0.76)
          } !important`,
          backgroundImage: "none !important",
          boxShadow: `${theme.shadows[16]} !important`,
          border: "none !important",
          borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 !important`,
          outline: "none !important",
          color: theme.palette.text.primary,
          overflowX: "hidden",
          backdropFilter: "blur(22px) saturate(135%)",
          WebkitBackdropFilter: "blur(22px) saturate(135%)",
        },
        "& .MuiBackdrop-root": {
          backgroundColor: alpha(theme.palette.common.black, 0.58),
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
        },
      }
    : {
        width: drawerWidth,
        "& .MuiDrawer-paper": {
          background: "transparent !important",
          backgroundImage: "none !important",
          boxShadow: "none !important",
          border: "none !important",
          outline: "none !important",
          color: theme.palette.text.primary,
          overflowX: "hidden",
        },
        ...(open
          ? {
              ...openedMixin(theme),
              "& .MuiDrawer-paper": {
                ...openedMixin(theme),
                background: "transparent !important",
                backgroundImage: "none !important",
                boxShadow: "none !important",
                border: "none !important",
              },
            }
          : {
              ...closedMixin(theme),
              "& .MuiDrawer-paper": {
                ...closedMixin(theme),
                background: "transparent !important",
                backgroundImage: "none !important",
                boxShadow: "none !important",
                border: "none !important",
              },
            }),
      }),
}));

const filters = [
  { name: "Activas", id: "actives" },
  { name: "Inactivas", id: "inactivates" },
  { name: "Todas", id: "all" },
];

function SpeechMuteButton() {
  const [speechActive, setSpeechActive] = React.useState(false);

  const syncSpeechState = React.useCallback(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) {
      setSpeechActive(false);
      return;
    }

    const synthesis = window.speechSynthesis;
    setSpeechActive(Boolean(synthesis.speaking || synthesis.pending));
  }, []);

  React.useEffect(() => {
    syncSpeechState();
    const interval = window.setInterval(syncSpeechState, 180);

    return () => {
      window.clearInterval(interval);
    };
  }, [syncSpeechState]);

  const handleMute = React.useCallback(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    setSpeechActive(false);
  }, []);

  if (!speechActive) return null;

  return (
    <Box className="speech-mute-container">
      <IconButton
        className="speech-mute-button"
        onClick={handleMute}
        aria-label="Silenciar voz"
        title="Silenciar voz"
      >
        <Box className="speech-wave-orb">
          <span className="speech-wave speech-wave-1" />
          <span className="speech-wave speech-wave-2" />
          <span className="speech-wave speech-wave-3" />
          <span className="speech-wave speech-wave-4" />
          <VolumeOffIcon className="speech-mute-icon" />
        </Box>
      </IconButton>
    </Box>
  );
}

export default function MiniDrawer(props) {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [filter, setFilter] = React.useState(filters[0]);
  const [ref, bounds] = useMeasure();
  const [appBarActionsTarget, setAppBarActionsTarget] = React.useState(null);

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  const isMd = useMediaQuery(theme.breakpoints.up("sm"), {
    defaultMatches: true,
  });
  const isWideAppBar = useMediaQuery(theme.breakpoints.up("md"), {
    noSsr: true,
  });
  const history = useHistory();
  const [path, setPath] = React.useState(history.location.pathname);
  const isCalendarRoute = path === "/calendar";
  const stackCalendarAppBar = isCalendarRoute && (!isWideAppBar || open);

  const token = useSelector((state) => state.session.token);
  const user = useSelector((state) => state.session.globalUser);
  const institution = useSelector((state) => state.session.institution);
  const toolsByUser = useSelector((state) => state.session.toolsByUser || {});
  const activeApplication = useSelector(
    (state) => state.session.activeApplication,
  );

  const [roles, setRoles] = React.useState([]);
  const [validateDelete, setValidateDelete] = React.useState(null);
  const [routesFilter, setRoutesFilter] = React.useState([]);
  const [institutions, setInstitutions] = React.useState([]);
  const [showLogin, setShowLogin] = React.useState(!token);
  const [socketOnline, setSocketOnline] = React.useState(!!socket?.connected);
  const [socketConnecting, setSocketConnecting] = React.useState(false);
  const [connectionError, setConnectionError] = React.useState(false);
  const [foregroundPush, setForegroundPush] = React.useState(null);
  const [toolSelection, setToolSelection] = React.useState(defaultToolIds);
  const [applicationsSelectorSource, setApplicationsSelectorSource] =
    React.useState(null);
  const reconnectInFlightRef = React.useRef(null);

  const dispatch = useDispatch();

  const rolesService = app.service("users-roles");
  const institutionService = app.service("institutions");

  const userPreferenceId = getUserPreferenceId(user);
  const savedToolIds = userPreferenceId
    ? toolsByUser?.[userPreferenceId]
    : null;
  const hasSavedTools = Array.isArray(savedToolIds) && savedToolIds.length > 0;
  const selectedToolIds = hasSavedTools ? savedToolIds : defaultToolIds;
  const hasActiveApplication = ["tgone", "cemedi"].includes(activeApplication);
  const showToolsSelector =
    !!token && !showLogin && !!userPreferenceId && !hasSavedTools;
  const showInstitutionSelector =
    !!token && !showLogin && !showToolsSelector && !institution;
  const showApplicationsSelector = !!applicationsSelectorSource;
  const showInitialDashboardSelector =
    !!token &&
    !showLogin &&
    !showToolsSelector &&
    !!institution &&
    !!userPreferenceId &&
    (!hasActiveApplication || showApplicationsSelector);

  React.useEffect(() => {
    if (!!token && showLogin) {
      setShowLogin(false);
    } else if (!token && !showLogin) {
      setShowLogin(true);
    }

    return history.listen((location) => {
      if (!token && location.pathname !== "/") {
        setShowLogin(true);
      } else if (location.pathname !== "/" && !showLogin) {
        setShowLogin(false);
      }
      setPath(location.pathname);
    });
  }, [history, token]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const handleSocketConnect = () => {
      setSocketOnline(false);
      setSocketConnecting(true);
    };
    const handleSocketAuthenticated = () => {
      if (!socket.connected) return;
      setSocketOnline(true);
      setSocketConnecting(false);
    };
    const handleSocketDisconnect = () => {
      setSocketOnline(false);
      setSocketConnecting(false);
    };
    const handleSocketError = () => {
      setSocketOnline(false);
      setSocketConnecting(false);
    };
    const handleReconnectAttempt = () => {
      setSocketOnline(false);
      setSocketConnecting(true);
    };

    socket.on("connect", handleSocketConnect);
    socket.on("disconnect", handleSocketDisconnect);
    socket.on("connect_error", handleSocketError);
    socket.on("reconnect_attempt", handleReconnectAttempt);
    socketApp.on("authenticated", handleSocketAuthenticated);

    return () => {
      socket.off("connect", handleSocketConnect);
      socket.off("disconnect", handleSocketDisconnect);
      socket.off("connect_error", handleSocketError);
      socket.off("reconnect_attempt", handleReconnectAttempt);
      socketApp.removeListener("authenticated", handleSocketAuthenticated);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    let mounted = true;

    if (!token)
      return () => {
        mounted = false;
      };

    setSocketOnline(false);
    setSocketConnecting(true);
    reconnectAndAuthenticate({ socket, socketApp, token })
      .then(() => {
        if (!mounted) return;
        setSocketOnline(true);
        setSocketConnecting(false);
      })
      .catch(() => {
        if (!mounted) return;
        setSocketOnline(false);
        setSocketConnecting(false);
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  React.useEffect(
    () => registerForegroundPushHandler(setForegroundPush),
    [],
  );

  const reconnectSocket = React.useCallback(async () => {
    if (reconnectInFlightRef.current) return reconnectInFlightRef.current;

    setConnectionError(false);
    setSocketOnline(false);
    setSocketConnecting(true);
    const attempt = reconnectAndAuthenticate({
      socket,
      socketApp,
      token,
      forceTransportReconnect: !socket.connected,
    });
    reconnectInFlightRef.current = attempt;
    try {
      await attempt;
      setSocketOnline(true);
    } catch (_error) {
      setSocketOnline(false);
      setConnectionError(true);
    } finally {
      setSocketConnecting(false);
      if (reconnectInFlightRef.current === attempt) {
        reconnectInFlightRef.current = null;
      }
    }
  }, [token]);

  const socketIndicatorSx = React.useMemo(
    () => ({
      display: "inline-flex",
      alignItems: "center",
      gap: 0.8,
      px: 1.2,
      py: 0.55,
      borderRadius: 999,
      border: "1px solid",
      borderColor: socketOnline
        ? "rgba(76, 175, 80, 0.45)"
        : "rgba(244, 67, 54, 0.45)",
      color: socketOnline ? "#7CFF8A" : "#FF6B6B",
      backgroundColor: socketOnline
        ? "rgba(76, 175, 80, 0.08)"
        : "rgba(244, 67, 54, 0.08)",
      boxShadow: socketOnline
        ? "0 0 8px rgba(76, 175, 80, 0.24), 0 0 16px rgba(76, 175, 80, 0.14)"
        : "0 0 8px rgba(244, 67, 54, 0.24), 0 0 16px rgba(244, 67, 54, 0.14)",
      animation: "socket-neon-pulse 1.8s ease-in-out infinite",
      cursor: socketOnline ? "default" : "pointer",
      userSelect: "none",
      transition:
        "box-shadow 160ms ease, background-color 160ms ease, border-color 160ms ease",
      "&:hover": socketOnline
        ? undefined
        : {
            boxShadow:
              "0 0 10px rgba(244, 67, 54, 0.32), 0 0 18px rgba(244, 67, 54, 0.2)",
          },
      "@keyframes socket-neon-pulse": {
        "0%, 100%": {
          boxShadow: socketOnline
            ? "0 0 8px rgba(76, 175, 80, 0.24), 0 0 16px rgba(76, 175, 80, 0.14)"
            : "0 0 8px rgba(244, 67, 54, 0.24), 0 0 16px rgba(244, 67, 54, 0.14)",
          opacity: 0.94,
        },
        "50%": {
          boxShadow: socketOnline
            ? "0 0 14px rgba(76, 175, 80, 0.4), 0 0 24px rgba(76, 175, 80, 0.24)"
            : "0 0 14px rgba(244, 67, 54, 0.4), 0 0 24px rgba(244, 67, 54, 0.24)",
          opacity: 1,
        },
      },
    }),
    [socketOnline],
  );

  const socketDotSx = React.useMemo(
    () => ({
      width: 10,
      height: 10,
      borderRadius: "50%",
      flexShrink: 0,
      backgroundColor: socketOnline ? "#7CFF8A" : "#FF6B6B",
      boxShadow: socketOnline
        ? "0 0 6px rgba(124, 255, 138, 0.8), 0 0 14px rgba(76, 175, 80, 0.42)"
        : "0 0 6px rgba(255, 107, 107, 0.82), 0 0 14px rgba(244, 67, 54, 0.42)",
      animation: "socket-dot-glow 1.8s ease-in-out infinite",
      "@keyframes socket-dot-glow": {
        "0%, 100%": {
          boxShadow: socketOnline
            ? "0 0 6px rgba(124, 255, 138, 0.8), 0 0 14px rgba(76, 175, 80, 0.42)"
            : "0 0 6px rgba(255, 107, 107, 0.82), 0 0 14px rgba(244, 67, 54, 0.42)",
          opacity: 0.9,
        },
        "50%": {
          boxShadow: socketOnline
            ? "0 0 10px rgba(124, 255, 138, 0.95), 0 0 18px rgba(76, 175, 80, 0.58)"
            : "0 0 10px rgba(255, 107, 107, 0.95), 0 0 18px rgba(244, 67, 54, 0.58)",
          opacity: 1,
        },
      },
    }),
    [socketOnline],
  );

  const logging = React.useCallback(async () => {
    const result = await requestAndSyncPushToken(user);
    console.log("push ---->", result);
    if (
      !result.ok &&
      !["permission-denied", "unsupported", "missing-user"].includes(
        result.reason,
      )
    ) {
      console.warn(
        "No se pudo sincronizar token push:",
        result.reason || result.error,
      );
    }
  }, [user]);

  const getInstitutions = async () => {
    setInstitutions([]);
    let params = undefined;

    switch (filter.id) {
      case "actives":
        params = { query: { bl_activo: true } };
        break;
      case "inactivates":
        params = { query: { bl_activo: false } };
        break;
      default:
        params = undefined;
    }

    try {
      const res = await institutionService.find(params);
      setInstitutions(res?.data || []);
    } catch (error) {
      if (error?.name === "NotAuthenticated" || error?.code === 401) {
        setShowLogin(true);
        dispatch({ type: "UNSET_USER", payload: null });
        return;
      }
    }
  };

  const getRoles = async () => {
    setRoles([]);
    try {
      const userId = user?.id;
      if (!userId) return;
      const res = await rolesService.find({
        query: { $and: [{ id_usuario: userId }] },
      });
      setRoles(res?.data || []);
    } catch (error) {
      if (error?.name === "NotAuthenticated" || error?.code === 401) {
        setShowLogin(true);
        dispatch({ type: "UNSET_USER", payload: null });
        return;
      }
    }
  };

  React.useEffect(() => {
    if (token) {
      getInstitutions();
      getRoles();
      logging();
    }
  }, [token, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (showToolsSelector) {
      setToolSelection(defaultToolIds);
    }
  }, [showToolsSelector, userPreferenceId]);

  const toggleToolSelection = React.useCallback((toolId) => {
    setToolSelection((current) => {
      if (current.includes(toolId)) {
        return current.filter((id) => id !== toolId);
      }
      return [...current, toolId];
    });
  }, []);

  const openApplicationsSelector = React.useCallback(() => {
    setApplicationsSelectorSource("manual");
  }, []);

  const handleSaveTools = React.useCallback(() => {
    if (!userPreferenceId || toolSelection.length === 0) return;
    dispatch(setUserTools({ userId: userPreferenceId, tools: toolSelection }));
  }, [dispatch, toolSelection, userPreferenceId]);

  const getInstitutionSession = React.useCallback(
    (ins) => {
      return {
        ...ins,
        roles: roles.filter(
          (role) =>
            role.id_institucion === ins.id ||
            role.id_institucion === "Global" ||
            role.id_institucion === null,
        ),
      };
    },
    [roles],
  );

  const handleSelectInstitution = React.useCallback(
    (ins) => {
      if (!ins?.bl_activo) return;
      dispatch(setInstitution(getInstitutionSession(ins)));
      setApplicationsSelectorSource("institution");
    },
    [dispatch, getInstitutionSession],
  );

  const handleChangeInstitution = React.useCallback(() => {
    setApplicationsSelectorSource(null);
    dispatch(setInstitution(null));
  }, [dispatch]);

  const handleSelectInitialDashboard = React.useCallback(
    (dashboard) => {
      if (!userPreferenceId) return;
      dispatch(setActiveApplication(dashboard.id));
      setApplicationsSelectorSource(null);
      history.push(dashboard.path);
    },
    [dispatch, history, userPreferenceId],
  );

  const initialNavigationHandled = React.useRef(false);
  React.useEffect(() => {
    initialNavigationHandled.current = false;
  }, [userPreferenceId]);

  React.useEffect(() => {
    if (
      !token ||
      !institution ||
      !hasActiveApplication ||
      initialNavigationHandled.current
    )
      return;
    initialNavigationHandled.current = true;
    if (["/", "/login", "/register"].includes(history.location.pathname)) {
      history.replace(getInitialDashboardPath(activeApplication));
    }
  }, [token, institution, hasActiveApplication, history, activeApplication]);

  React.useEffect(() => {
    if (!token || !institution || !hasActiveApplication) return;
    const currentRoute = routes.find((route) => route.path === path);
    if (
      currentRoute?.applications &&
      !currentRoute.applications.includes(activeApplication)
    ) {
      history.replace(getInitialDashboardPath(activeApplication));
    }
  }, [
    activeApplication,
    hasActiveApplication,
    history,
    institution,
    path,
    token,
  ]);

  React.useEffect(() => {
    const roleCandidates = [...(roles || []), ...(institution?.roles || [])];

    const isClientByRole = roleCandidates.some((r) => {
      const roleName = `${
        r?.rol?.vc_nombre ||
        r?.role?.vc_nombre ||
        r?.vc_nombre ||
        r?.rol_nombre ||
        ""
      }`.toLowerCase();
      const roleInstitution =
        r?.id_institucion || r?.rol?.id_institucion || null;
      const sameInstitution =
        !institution?.id ||
        !roleInstitution ||
        roleInstitution === institution.id ||
        roleInstitution === "Global";
      return roleName.includes("cliente") && sameInstitution;
    });
    const selectedTools = new Set(selectedToolIds);

    setRoutesFilter(
      routes.filter(
        (e) =>
          (!e.applications || e.applications.includes(activeApplication)) &&
          (!e.clientOnly || isClientByRole) &&
          (e.required || !hasSavedTools || selectedTools.has(e.id)) &&
          (e.allowRoles === undefined ||
            !!e.allowRoles?.filter((i) =>
              (institution?.roles?.map((x) => x.rol?.id) || []).includes(i),
            ).length ||
            !!e.allowRoles?.filter((i) =>
              (roles?.map((x) => x.rol?.id) || []).includes(i),
            ).length),
      ),
    );
  }, [roles, institution, hasSavedTools, selectedToolIds, activeApplication]);

  const routeSelected =
    routes.find((e) => history.location.pathname === e.path) ||
    routes.find((e) => e.applications?.includes(activeApplication)) ||
    routes[0];

  const renderHeaderButton = () => {
    return (
      <Box
        key="Container Button"
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <IconButton
          key={"Applications Button"}
          color="inherit"
          onClick={openApplicationsSelector}
          disabled={!userPreferenceId}
          title="Aplicaciones"
          sx={{
            color: theme.palette.text.primary,
            backgroundColor: "transparent",
          }}
        >
          <AppsIcon />
        </IconButton>
        <NotificationComponent key={"Notification Button"} />
        <CatalogsIconButton
          key={"Account Button"}
          model={usuarios}
          icon={"/assets/icons/avatar.svg"}
          item={user}
          color={"white"}
          backgroundColor={"transparent"}
        />
        <Box
          onClick={!socketOnline ? reconnectSocket : undefined}
          sx={{
            ...(isMd
              ? {
                  ...socketIndicatorSx,
                  px: 1,
                  py: 0.38,
                  gap: 0.7,
                  backgroundColor: "transparent",
                  borderColor: "transparent",
                  boxShadow: "none",
                }
              : {
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  cursor: socketOnline ? "default" : "pointer",
                }),
          }}
          title={
            socketOnline
              ? "Socket online"
              : socketConnecting
                ? "Reconectando socket. Clic para reintentar"
                : "Socket offline. Clic para reconectar"
          }
        >
          <Box sx={socketDotSx} />
          {isMd && (
            <Typography
              fontSize={12}
              fontWeight={600}
              sx={{
                color: socketOnline
                  ? "rgba(124, 255, 138, 0.92)"
                  : "rgba(255, 107, 107, 0.92)",
                letterSpacing: 0.2,
              }}
            >
              {socketOnline
                ? "Online"
                : socketConnecting
                  ? "Conectando…"
                  : "Offline"}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const isSystem = !!roles.find((e) => e.rol?.vc_nombre === "Sistema");
  const isAdmin = !!roles.find((e) => e.rol?.vc_nombre === "Admin");

  return [
    !institution && (
      <Paper
        key="bg"
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          borderRadius: 0,
          background: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      />
    ),

    (!!institution || history.location.pathname === "/video-call") && (
      <Box
        key="miniDrawer"
        sx={{ display: "flex", height: "100%", width: "100%" }}
      >
        <Helmet>
          <title>{`TGONE | ${path}`}</title>
          <meta name="description" content="Helmet application" />
        </Helmet>

        <CssBaseline />

        {/* Header flotante transparente */}
        <AppBar
          position="fixed"
          open={open}
          mobile={!isMd}
          elevation={0}
        >
          <Toolbar
            ref={ref}
            sx={{
              alignContent: "center",
              border: "none",
              backgroundColor: "transparent",
              boxSizing: "border-box",
              flexWrap: stackCalendarAppBar ? "wrap" : "nowrap",
              width: "100%",
              minHeight: stackCalendarAppBar ? 94 : isMd ? 64 : 50,
              py: isMd ? 0 : 0.25,
            }}
          >
            <Box
              className="appbar-primary-row"
              sx={{
                alignItems: "center",
                display: "flex",
                flex: "0 0 auto",
                minHeight: stackCalendarAppBar ? 46 : "auto",
                minWidth: 0,
                width: stackCalendarAppBar ? "100%" : "auto",
              }}
            >
              <IconButton
                key="Drawer Button"
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                edge="start"
                sx={{
                  marginRight: 2,
                  ...(open && { display: "none" }),
                }}
              >
                <MenuIcon />
              </IconButton>

              <Typography variant="h6" noWrap component="div">
                {routeSelected?.name || ""}
              </Typography>
            </Box>

            <Box
              className="appbar-view-actions"
              ref={setAppBarActionsTarget}
              sx={{
                alignItems: "center",
                display: isCalendarRoute ? "flex" : "none",
                flex: stackCalendarAppBar ? "0 0 100%" : "1 1 auto",
                minHeight: stackCalendarAppBar ? 44 : "auto",
                minWidth: 0,
                pl: stackCalendarAppBar ? 0 : 2,
                pr: stackCalendarAppBar ? 0 : "220px",
                width: stackCalendarAppBar ? "100%" : "auto",
              }}
            />

            <Grid
              key="Right Buttons"
              container
              item
              xs="auto"
              position="absolute"
              direction={"row"}
              right={8}
              top={stackCalendarAppBar ? 4 : undefined}
              display={open && !isMd ? "none" : "flex"}
              alignItems="center"
            >
              {renderHeaderButton()}
            </Grid>
          </Toolbar>
        </AppBar>

        {/* Drawer permanente en escritorio y modal en mobile */}
        <Drawer
          variant={isMd ? "permanent" : "temporary"}
          open={open}
          mobile={!isMd}
          onClose={!isMd ? handleDrawerClose : undefined}
          ModalProps={!isMd ? { keepMounted: true } : undefined}
        >
          <DrawerHeader>
            <ListItem
              key={"title"}
              disablePadding
              sx={{
                display: "block",
                position: "absolute",
                left: 0,
                top: 8,
              }}
            >
              <ListItemButton
                disableRipple
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  borderRadius: 2,
                  // nada de background en el contenedor del drawer
                  backgroundColor: "transparent",
                }}
                onClick={() => {
                  handleChangeInstitution();
                  if (!isMd) handleDrawerClose();
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : "auto",
                    justifyContent: "center",
                    opacity: open ? 1 : 0,
                  }}
                >
                  <img
                    src="assets/images/logo192.png"
                    style={{
                      backgroundColor: "white",
                      height: 35,
                      padding: 2,
                      borderRadius: "50%",
                    }}
                    alt=""
                  />
                </ListItemIcon>
                <ListItemText
                  className="text-container"
                  primary={`${institution?.vc_nombre || "TGONE"}`}
                  sx={{ opacity: open ? 1 : 0, overflow: "hidden" }}
                />
              </ListItemButton>
            </ListItem>

            {open && (
              <IconButton onClick={handleDrawerClose}>
                {theme.direction === "rtl" ? (
                  <ChevronRightIcon />
                ) : (
                  <ChevronLeftIcon />
                )}
              </IconButton>
            )}
          </DrawerHeader>

          {/* Divider transparente (sin borde) */}
          <Divider sx={{ opacity: 0, border: "none" }} />

          <List sx={{ pt: 1 }}>
            {routesFilter
              .filter((e) => !e.hidden)
              .map((route, index) => {
                const selected = route.path === path;
                return (
                  <ListItem
                    key={index}
                    disablePadding
                    sx={{
                      display: "block",
                      backgroundColor: "transparent",
                    }}
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                        borderRadius: 2,
                        color: selected
                          ? theme.palette.primary.main
                          : theme.palette.text.primary,
                        backgroundColor: selected
                          ? theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.10)"
                            : "rgba(24,39,63,0.10)"
                          : "transparent",
                        "&:hover": {
                          backgroundColor: selected
                            ? theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.14)"
                              : "rgba(24,39,63,0.14)"
                            : theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.06)"
                              : "rgba(24,39,63,0.06)",
                        },
                      }}
                      onClick={() => {
                        history.push(route.path);
                        if (!isMd) handleDrawerClose();
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : "auto",
                          justifyContent: "center",
                        }}
                      >
                        <IconMask
                          color={
                            selected
                              ? theme.palette.primary.main
                              : theme.palette.text.secondary
                          }
                          url={route.icon}
                          size={25}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={route.name}
                        sx={{ opacity: open ? 1 : 0 }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
          </List>
        </Drawer>

        {/* Content */}
        <Box
          component="main"
          style={{ paddingTop: bounds.height, width: "100%" }}
          sx={{
            flexGrow: 1,
            p: 0,
            height: "100%",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <AppBarActionsContext.Provider value={appBarActionsTarget}>
            {props.children}
          </AppBarActionsContext.Provider>
        </Box>

        {isMd && (
          <Box p={1} position={"absolute"} bottom={0} right={0}>
            <Typography
              fontSize={10}
              style={{ color: theme.palette.text.secondary }}
            >
              {`Usuario: ${user?.vc_nombre} ${user?.vc_paterno || ""}`}
            </Typography>
            <Typography
              fontSize={10}
              style={{
                color: theme.palette.text.secondary,
                textAlign: "right",
              }}
            >
              {`Version: ${process.env.REACT_APP_VERSION || "N/D"}`}
            </Typography>
          </Box>
        )}
        <SpeechMuteButton />
      </Box>
    ),

    <Login
      key={"login-selector"}
      open={showLogin}
      setShowLogin={setShowLogin}
      version={
        <Box
          p={1}
          position={"absolute"}
          bottom={0}
          right={0}
          style={{ zIndex: 9999 }}
        >
          <Typography
            fontSize={10}
            style={{ color: theme.palette.text.secondary }}
          >
            {`Usuario: ${user?.vc_nombre} ${user?.vc_paterno || ""}`}
          </Typography>
          <Typography
            fontSize={10}
            style={{ color: theme.palette.text.secondary, textAlign: "right" }}
          >
            {`Version: ${process.env.REACT_APP_VERSION || "N/D"}`}
          </Typography>
        </Box>
      }
    />,

    <Dialog
      key={"tools-selector"}
      open={showToolsSelector}
      TransitionComponent={TransitionUp}
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        },
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{"Selecciona las herramientas que usarás"}</DialogTitle>
      <DialogContent style={{ maxHeight: isMd ? "60vh" : "100%" }}>
        {selectableToolRoutes.map((route) => {
          const checked = toolSelection.includes(route.id);
          return (
            <ListItemButton
              key={route.id}
              onClick={() => toggleToolSelection(route.id)}
              sx={{ borderRadius: 1 }}
            >
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={checked}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <IconMask
                  color={theme.palette.mode === "dark" ? "white" : "black"}
                  url={route.icon}
                  size={24}
                />
              </ListItemIcon>
              <ListItemText primary={route.name} />
            </ListItemButton>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          disabled={toolSelection.length === 0}
          onClick={handleSaveTools}
        >
          {"Continuar"}
        </Button>
      </DialogActions>
    </Dialog>,

    <Dialog
      key={"institution-selector"}
      open={showInstitutionSelector}
      TransitionComponent={TransitionUp}
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        },
      }}
    >
      <DialogTitle>{"Para iniciar selecciona o crea una empresa"}</DialogTitle>
      <DialogContent style={{ height: isMd ? "50vh" : "100%" }}>
        <ButtonGroup
          fullWidth
          size="small"
          style={{ marginBottom: 16, borderRadius: 30, overflow: "hidden" }}
        >
          {filters.map((item) => (
            <Button
              variant={item.id === filter.id ? "contained" : "outlined"}
              key={item.id}
              onClick={() => setFilter(item)}
              style={{ border: "none" }}
            >
              {item.name}
            </Button>
          ))}
        </ButtonGroup>

        {institutions.map((ins) => (
          <ListItemButton
            key={ins.id}
            onClick={() => {
              handleSelectInstitution(ins);
            }}
          >
            <Box paddingRight={3} style={{ opacity: ins.bl_activo ? 1 : 0.5 }}>
              <IconMask
                color={theme.palette.mode === "dark" ? "white" : "black"}
                url={"/assets/icons/fabrica.svg"}
                size={25}
              />
            </Box>
            <Typography style={{ opacity: ins.bl_activo ? 1 : 0.5 }}>
              {ins.vc_nombre}
            </Typography>

            {(!!isAdmin || !!isSystem) && (
              <ListItemSecondaryAction
                onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
                onMouseLeave={() => setAnchorEl(null)}
              >
                {ins.bl_activo ? (
                  <CatalogsIconButton
                    model={instituciones}
                    icon={"/assets/icons/settings.svg"}
                    item={ins}
                    color={theme.palette.text.primary}
                    backgroundColor={"transparent"}
                    size={20}
                    endButtons={
                      <Button
                        {...(!isSystem
                          ? {
                              disabled: true,
                              onMouseEnter: (e) => setAnchorEl(e.currentTarget),
                              onMouseLeave: () => setAnchorEl(null),
                            }
                          : {})}
                        variant="contained"
                        style={{ backgroundColor: "red" }}
                        onClick={() => {
                          setValidateDelete({
                            ...ins,
                            action: () => {
                              institutionService.patch(ins.id, {
                                bl_activo: false,
                              });
                            },
                          });
                        }}
                      >
                        <DeleteForeverOutlined />
                      </Button>
                    }
                  />
                ) : (
                  <CatalogsIconButton
                    model={instituciones}
                    icon={"/assets/icons/settings.svg"}
                    item={ins}
                    color={theme.palette.text.primary}
                    backgroundColor={"transparent"}
                    size={20}
                    endButtons={
                      <Button
                        {...(!isSystem
                          ? {
                              disabled: true,
                              onMouseEnter: (e) => setAnchorEl(e.currentTarget),
                              onMouseLeave: () => setAnchorEl(null),
                            }
                          : {})}
                        variant="contained"
                        onClick={() => {
                          setValidateDelete({
                            ...ins,
                            action: () => {
                              institutionService.patch(ins.id, {
                                bl_activo: true,
                              });
                            },
                          });
                        }}
                      >
                        {"Activar"}
                      </Button>
                    }
                  />
                )}

                {!isSystem && (
                  <Popover
                    id="mouse-over-popover"
                    sx={{ pointerEvents: "none" }}
                    open={!!anchorEl}
                    anchorEl={anchorEl}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                    onClose={() => setAnchorEl(null)}
                    disableRestoreFocus
                  >
                    <Typography sx={{ p: 1 }}>
                      Comunicate con TGOne para reactivar esta empresa
                    </Typography>
                  </Popover>
                )}
              </ListItemSecondaryAction>
            )}
          </ListItemButton>
        ))}

        {institutions.length === 0 && <Button>{"!No hay resultados¡"}</Button>}
      </DialogContent>

      <DialogActions>
        <CatalogsIconButton
          key={"catIcon"}
          title={"Crear"}
          mode="title"
          variant="text"
          model={instituciones}
        />
        <Button
          key={"aceptar"}
          onClick={() => {
            dispatch(removeToken());
            dispatch(removeGlobalUser());
            dispatch(removeInstitution());
            setApplicationsSelectorSource(null);
            setInstitutions([]);
          }}
        >
          {"Cerrar Sesión"}
        </Button>
      </DialogActions>
    </Dialog>,

    <Dialog
      key={"initial-dashboard-selector"}
      open={showInitialDashboardSelector}
      TransitionComponent={TransitionUp}
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        },
      }}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>{"Selecciona la aplicación"}</DialogTitle>
      <DialogContent>
        {applicationOptions.map((dashboard) => (
          <ListItemButton
            key={dashboard.id}
            onClick={() => handleSelectInitialDashboard(dashboard)}
            sx={{
              borderRadius: 2,
              mb: 1,
              border: "1px solid",
              borderColor:
                activeApplication === dashboard.id
                  ? theme.palette.primary.main
                  : "rgba(255,255,255,0.12)",
            }}
          >
            <ListItemIcon sx={{ minWidth: 48 }}>
              <Box
                component="img"
                src={dashboard.image}
                alt=""
                sx={{
                  width: 34,
                  height: 34,
                  objectFit: "contain",
                  borderRadius: "50%",
                  backgroundColor: "white",
                  p: 0.4,
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary={dashboard.name}
              secondary={dashboard.description}
              secondaryTypographyProps={{ color: "text.secondary" }}
            />
          </ListItemButton>
        ))}
      </DialogContent>
      {applicationsSelectorSource === "manual" && (
        <DialogActions>
          <Button onClick={() => setApplicationsSelectorSource(null)}>
            {"Cancelar"}
          </Button>
        </DialogActions>
      )}
    </Dialog>,

    <Dialog key={"validate"} open={!!validateDelete} TransitionComponent={Zoom}>
      <DialogTitle>{`Desea eliminar ${validateDelete?.vc_nombre}`}</DialogTitle>
      <DialogContent>
        {`¿Esta acción ${
          validateDelete?.bl_activo ? "inactivará" : "reactivará"
        } nuevos eventos y registros de sus dispositivos?`}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setValidateDelete(null)}>{"Cancelar"}</Button>
        <Button
          variant="contained"
          color={validateDelete?.bl_activo ? "error" : "primary"}
          onClick={async () => {
            await validateDelete?.action();
            setValidateDelete(null);
            setInstitution([]);
            getInstitutions();
          }}
        >
          {validateDelete?.bl_activo ? "Eliminar" : "Activar"}
        </Button>
      </DialogActions>
    </Dialog>,
    <Snackbar
      key="socket-reconnect-error"
      open={connectionError}
      autoHideDuration={5000}
      onClose={() => setConnectionError(false)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        severity="error"
        variant="filled"
        onClose={() => setConnectionError(false)}
      >
        {
          "No fue posible restablecer la conexión. Verifica tu red e inténtalo nuevamente"
        }
      </Alert>
    </Snackbar>,
    <Snackbar
      key="foreground-push-notification"
      open={Boolean(foregroundPush)}
      autoHideDuration={10000}
      onClose={(_event, reason) => {
        if (reason !== "clickaway") setForegroundPush(null);
      }}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert
        severity="info"
        variant="filled"
        onClose={() => setForegroundPush(null)}
        action={
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const target = new URL(
                resolveNotificationLink(getPayloadLink(foregroundPush)),
              );
              setForegroundPush(null);

              if (target.origin === window.location.origin) {
                history.push(`${target.pathname}${target.search}${target.hash}`);
                return;
              }

              window.location.assign(target.href);
            }}
            sx={{
              color: theme.palette.primary.main,
              borderColor: alpha(theme.palette.primary.main, 0.55),
              fontWeight: 700,
              "&:hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            Ver
          </Button>
        }
        icon={false}
        sx={{
          alignItems: "center",
          minWidth: { xs: 280, sm: 420 },
          maxWidth: { xs: "calc(100vw - 32px)", sm: 520 },
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.background.paper,
          backgroundImage: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.14,
          )}, transparent 58%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.42)}`,
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          boxShadow: `0 14px 38px ${alpha(
            theme.palette.common.black,
            theme.palette.mode === "dark" ? 0.48 : 0.2,
          )}`,
          "& .MuiAlert-action": {
            alignItems: "center",
            paddingTop: 0,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            component="img"
            src="/logo192.png"
            alt="TGOne"
            sx={{
              width: 42,
              height: 42,
              flexShrink: 0,
              borderRadius: 1.5,
              objectFit: "contain",
              backgroundColor: alpha(theme.palette.common.white, 0.94),
              padding: 0.45,
              boxShadow: `0 4px 14px ${alpha(
                theme.palette.primary.main,
                0.24,
              )}`,
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {getPayloadTitle(foregroundPush)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getPayloadBody(foregroundPush) ||
                "Tienes una nueva notificación"}
            </Typography>
          </Box>
        </Box>
      </Alert>
    </Snackbar>,
  ];
}
